if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
          second: '[data-timer-second]',
        };
        this.units = ['day', 'hour', 'minute', 'second'];
        this.type = this.dataset.type;
        this.duration = this.dataset.duration;
        this.loop = false;
        this.shouldAddLeadingZero = true;

        this.initCountdown();
      }

      initCountdown() {
        switch (this.type) {
          case 'fixed_time':
            const countdownData = this.dataset.dueDate;
            if (countdownData) {
              this.targetTime = this.parseParisDueDate(countdownData);
            }
            break;
          default: // Evergreen.
            this.targetTime = this.getEvergreenDueDate();
            break;
        }

        if (!this.targetTime) {
          return;
        }

        this.startTime = Date.now();
        if (this.targetTime > this.startTime) {
          this.fetchDomElements();
          this.beginCountdown();
        }
      }

      fetchDomElements() {
        this.elements = {};
        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });
      }

      beginCountdown() {
        this.intervalId = setInterval(() => {
          const remainingTime = this.targetTime - Date.now();
          if (remainingTime <= 0) {
            this.terminateCountdown();
          } else {
            this.updateDisplay(remainingTime);
          }
        }, 1000);
        this.classList.remove('hidden');
      }

      updateDisplay(remainingTime) {
        const timeComponents = this.computeTimeComponents(remainingTime);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
          }
        });
      }

      computeTimeComponents(ms) {
        return {
          day: Math.floor(ms / this.MS_IN_DAY),
          hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
          minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
      }

      terminateCountdown() {
        clearInterval(this.intervalId);
        if (this.loop) {
          this.beginCountdown();
        } else {
          this.classList.add('hidden');
        }
      }

      resetCountdown() {
        clearInterval(this.intervalId);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = '00';
          }
        });
      }

      // All countdown deadlines are meant as Europe/Paris wall-clock time, so that every
      // visitor worldwide sees the same real-world deadline instead of one relative to
      // their own browser timezone. The helpers below convert Paris wall-clock time to/from
      // UTC using Intl, since native Date parsing/getters only know the visitor's local zone.

      // A "pseudo" date whose UTC-suffixed fields hold Paris wall-clock values, so calendar
      // math (add a month, find next Sunday, etc.) can be done with plain setUTC*/getUTC*
      // calls instead of the visitor's local getters/setters.
      parisWallTimeToPseudoDate(year, month, day, hour, minute, second) {
        return new Date(Date.UTC(year, month, day, hour, minute, second || 0));
      }

      // Converts a pseudo date (see above) back to the real UTC timestamp of that Paris
      // wall-clock moment, accounting for CET/CEST by asking Intl what that instant reads
      // as in Europe/Paris and correcting for the difference.
      pseudoDateToParisUTC(pseudoDate) {
        const guessUTC = pseudoDate.getTime();
        const parts = this.getPartsInTimeZone(new Date(guessUTC), 'Europe/Paris');
        const parisAsUTC = Date.UTC(parts.year, parts.month, parts.day, parts.hour, parts.minute, parts.second);
        const offsetMs = parisAsUTC - guessUTC;
        return guessUTC - offsetMs;
      }

      getPartsInTimeZone(date, timeZone) {
        const formatted = new Intl.DateTimeFormat('en-US', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          weekday: 'short',
          hourCycle: 'h23',
        }).formatToParts(date);

        const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
        const values = {};
        formatted.forEach(({ type, value }) => {
          values[type] = value;
        });

        return {
          year: Number(values.year),
          month: Number(values.month) - 1,
          day: Number(values.day),
          hour: Number(values.hour),
          minute: Number(values.minute),
          second: Number(values.second),
          weekday: weekdayMap[values.weekday],
        };
      }

      getParisNowPseudoDate() {
        const parisNow = this.getPartsInTimeZone(new Date(), 'Europe/Paris');
        return {
          pseudoDate: this.parisWallTimeToPseudoDate(
            parisNow.year,
            parisNow.month,
            parisNow.day,
            parisNow.hour,
            parisNow.minute,
            parisNow.second
          ),
          weekday: parisNow.weekday,
        };
      }

      // Parses the merchant-entered "YYYY/MM/DD HH:MM" or "YYYY/MM/DD HH:MM AA" date
      // (see locales/*.schema.json timer.time.info) as Paris wall-clock time and returns
      // the matching UTC timestamp in ms, or null if it doesn't match the expected format.
      parseParisDueDate(value) {
        const match = value.trim().match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
        if (!match) {
          return null;
        }

        const [, year, month, day, rawHour, minute, meridiem] = match;
        let hour = Number(rawHour);
        if (meridiem) {
          const isPM = meridiem.toUpperCase() === 'PM';
          if (isPM && hour !== 12) hour += 12;
          if (!isPM && hour === 12) hour = 0;
        }

        const pseudoDate = this.parisWallTimeToPseudoDate(
          Number(year),
          Number(month) - 1,
          Number(day),
          hour,
          Number(minute)
        );
        return this.pseudoDateToParisUTC(pseudoDate);
      }

      getEvergreenDueDate() {
        let intervalHrs;
        const { pseudoDate: now, weekday } = this.getParisNowPseudoDate();
        const dueDate = new Date(now);

        switch (this.duration) {
          case 'every_month':
            dueDate.setUTCMonth(dueDate.getUTCMonth() + 1); // Next month.
            dueDate.setUTCDate(1); // Start from the 1st day of the next month.
            dueDate.setUTCHours(0, 0, 0, 0);
            return this.pseudoDateToParisUTC(dueDate);
          case 'every_week': {
            const daysUntilNextWeek = 7 - weekday;
            dueDate.setUTCDate(dueDate.getUTCDate() + daysUntilNextWeek); // Move to next Sunday.
            dueDate.setUTCHours(0, 0, 0, 0); // Set the time to 00:00:00.
            return this.pseudoDateToParisUTC(dueDate);
          }
          case 'every_2_hrs':
            intervalHrs = 2;
            break;
          case 'every_6_hrs':
            intervalHrs = 6;
            break;
          case 'every_12_hrs':
            intervalHrs = 12;
            break;
          case 'every_24_hrs':
            intervalHrs = 24;
            break;
        }

        const currentHour = now.getUTCHours();

        // Possible start points based on the interval.
        const startPoints = [];
        for (let i = 0; i < 24; i += intervalHrs) {
          startPoints.push(i);
        }

        // Find the next start point.
        for (let i = 0; i < startPoints.length; i++) {
          if (
            currentHour < startPoints[i] ||
            (currentHour === startPoints[i] && now.getUTCMinutes() === 0 && now.getUTCSeconds() === 0)
          ) {
            dueDate.setUTCHours(startPoints[i], 0, 0, 0);
            return this.pseudoDateToParisUTC(dueDate);
          }
        }

        // If no future point today, roll over to the first point tomorrow.
        dueDate.setUTCDate(dueDate.getUTCDate() + 1);
        dueDate.setUTCHours(startPoints[0], 0, 0, 0);
        return this.pseudoDateToParisUTC(dueDate);
      }
    }
  );
}
